import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { themeTokens, resetStyles, baseStyles } from '../../styles/tokens.css.js';
import { formatTime, formatDate } from '../../utils/timezone.js';
import { buildGoogleCalendarUrl, downloadICS, type CalendarEvent } from '../../utils/calendar.js';

@customElement('yebo-step-confirm')
export class YeboStepConfirm extends LitElement {
  static styles = [
    themeTokens,
    resetStyles,
    baseStyles,
    css`
      .confirm-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--yebo-space-lg);
        text-align: center;
        padding-top: var(--yebo-space-xl);
      }
      .check-icon {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: var(--yebo-success);
        color: var(--yebo-bg);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 28px;
      }
      .heading {
        font-size: var(--yebo-font-size-xl);
        font-weight: 600;
        color: var(--yebo-text-heading);
      }
      .details {
        background: var(--yebo-bg-elevated);
        border: 1px solid var(--yebo-border);
        border-radius: var(--yebo-radius);
        padding: var(--yebo-space-lg);
        width: 100%;
        max-width: 320px;
        display: flex;
        flex-direction: column;
        gap: var(--yebo-space-sm);
      }
      .detail-row {
        display: flex;
        justify-content: space-between;
        font-size: var(--yebo-font-size-sm);
      }
      .detail-label {
        color: var(--yebo-text-muted);
      }
      .detail-value {
        font-weight: 500;
        color: var(--yebo-text);
      }
      .conf-number {
        font-family: monospace;
        font-size: var(--yebo-font-size-lg);
        color: var(--yebo-primary);
        font-weight: 600;
      }
      /* Ticket framing — the confirmation number doubles as the ticket code. */
      .ticket-code-label {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }
      .detail-value.paid {
        color: var(--yebo-primary);
        font-weight: 700;
      }
      /* R10.7 — post-booking actions */
      .actions-block {
        display: flex;
        flex-direction: column;
        gap: var(--yebo-space-sm);
        width: 100%;
        max-width: 320px;
      }
      .action-row {
        display: flex;
        gap: var(--yebo-space-sm);
      }
      .action-row .btn { flex: 1; }
      .manage-link {
        font-size: var(--yebo-font-size-sm);
        color: var(--yebo-text-muted);
        text-align: center;
      }
      .manage-link a { color: var(--yebo-primary); text-decoration: underline; }
      @media (pointer: coarse) {
        .action-row .btn { min-height: 44px; }
      }
    `,
  ];

  @property({ type: String, attribute: 'appointment-id' }) appointmentId = '';
  @property({ type: String, attribute: 'confirmation-number' }) confirmationNumber = '';
  @property({ type: String, attribute: 'selected-slot' }) selectedSlot = '';
  @property({ type: String, attribute: 'selected-slot-end' }) selectedSlotEnd = '';
  @property({ type: String }) timezone = 'UTC';
  @property({ type: Number, attribute: 'party-size' }) partySize = 2;
  @property({ type: Number, attribute: 'kids-count' }) kidsCount = 0;
  @property({ type: String }) name = '';
  @property({ type: String, attribute: 'business-name' }) businessName = '';

  // R10.7 — Add-to-Calendar + tokened manage-booking link. Gated behind the V2
  // flag so the confirmation is unchanged when off.
  @property({ type: Boolean, attribute: 'recovery-enabled' }) recoveryEnabled = false;
  @property({ type: String, attribute: 'manage-booking-url' }) manageBookingUrl = '';

  // Prepaid event-ticket framing (additive — absent for normal bookings).
  @property({ type: Boolean }) ticketed = false;
  @property({ type: String, attribute: 'event-name' }) eventName = '';
  @property({ type: String, attribute: 'tier-label' }) tierLabel = '';
  @property({ type: Number, attribute: 'amount-cents' }) amountCents = 0;
  @property({ type: String }) currency = '';
  @property({ type: Boolean, attribute: 'show-time' }) showTime = true;
  /** #953 — a 100%-comped ticket order: nothing was charged, so say so. */
  @property({ type: Boolean }) comped = false;
  /** #954 — the discount on this order, from the server's checkout echo:
   *  "Buy 1 Get 1 Free" for the auto deal, "Promo code X" for a typed code. */
  @property({ type: String, attribute: 'discount-label' }) discountLabel = '';
  @property({ type: Number, attribute: 'discount-cents' }) discountCents = 0;

  private _formatAmount(cents: number, currency: string): string {
    const major = cents / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: (currency || 'USD').toUpperCase(),
      }).format(major);
    } catch {
      return `${(currency || 'USD').toUpperCase()} ${major.toFixed(2)}`;
    }
  }

  private get _guestsLabel(): string {
    return this.kidsCount > 0
      ? `${this.partySize - this.kidsCount} adult${this.partySize - this.kidsCount !== 1 ? 's' : ''}, ${this.kidsCount} kid${this.kidsCount !== 1 ? 's' : ''}`
      : `${this.partySize}`;
  }

  /** Build the calendar event from the confirmed booking. */
  private get _calendarEvent(): CalendarEvent | null {
    if (!this.selectedSlot) return null;
    const title = this.ticketed && this.eventName
      ? this.eventName
      : `Reservation${this.businessName ? ` at ${this.businessName}` : ''}`;
    const descParts = [
      this.confirmationNumber ? `Confirmation: ${this.confirmationNumber}` : '',
      `Party of ${this.partySize}`,
    ].filter(Boolean);
    return {
      title,
      start: this.selectedSlot,
      end: this.selectedSlotEnd || undefined,
      description: descParts.join(' · '),
      location: this.businessName || undefined,
      uid: this.confirmationNumber || this.appointmentId || undefined,
    };
  }

  private _addToCalendar() {
    const ev = this._calendarEvent;
    if (ev) downloadICS(ev, `reservation-${this.confirmationNumber || 'booking'}.ics`);
  }

  /** R10.7 — Add-to-Calendar (.ics + Google) and the manage-booking link. */
  private _renderActions() {
    if (!this.recoveryEnabled) return nothing;
    const ev = this._calendarEvent;
    const googleUrl = ev ? buildGoogleCalendarUrl(ev) : '';
    return html`
      <div class="actions-block">
        ${ev ? html`
          <div class="action-row">
            <button class="btn btn-secondary" @click=${this._addToCalendar}>Add to calendar</button>
            <a class="btn btn-secondary" href=${googleUrl} target="_blank" rel="noopener noreferrer">Google Calendar</a>
          </div>
        ` : nothing}
        ${this.manageBookingUrl ? html`
          <p class="manage-link">
            Need to change something?
            <a href=${this.manageBookingUrl} target="_blank" rel="noopener noreferrer">Manage your booking</a>
          </p>
        ` : nothing}
      </div>
    `;
  }

  render() {
    if (this.ticketed) return this._renderTicket();

    return html`
      <div class="confirm-container">
        <div class="check-icon" aria-hidden="true">&#10003;</div>
        <h2 class="heading">Booking confirmed!</h2>
        ${this.confirmationNumber
          ? html`<p class="conf-number">${this.confirmationNumber}</p>`
          : ''}
        <div class="details">
          ${this.selectedSlot ? html`
            <div class="detail-row">
              <span class="detail-label">Date</span>
              <span class="detail-value">${formatDate(this.selectedSlot, this.timezone)}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Time</span>
              <span class="detail-value">${formatTime(this.selectedSlot, this.timezone)}</span>
            </div>
          ` : ''}
          <div class="detail-row">
            <span class="detail-label">Guests</span>
            <span class="detail-value">${this._guestsLabel}</span>
          </div>
          ${this.name ? html`
            <div class="detail-row">
              <span class="detail-label">Name</span>
              <span class="detail-value">${this.name}</span>
            </div>
          ` : ''}
        </div>
        ${this._renderActions()}
      </div>
    `;
  }

  /** Ticketed purchase — present the result as a TICKET. */
  private _renderTicket() {
    return html`
      <div class="confirm-container">
        <div class="check-icon" aria-hidden="true">&#127915;</div>
        <h2 class="heading">You're going!</h2>
        ${this.confirmationNumber ? html`
          <div>
            <p class="ticket-code-label">Ticket code</p>
            <p class="conf-number">${this.confirmationNumber}</p>
          </div>
        ` : ''}
        <div class="details">
          ${this.eventName ? html`
            <div class="detail-row">
              <span class="detail-label">Event</span>
              <span class="detail-value">${this.eventName}</span>
            </div>
          ` : ''}
          ${this.tierLabel ? html`
            <div class="detail-row">
              <span class="detail-label">Ticket</span>
              <span class="detail-value">${this.tierLabel}</span>
            </div>
          ` : ''}
          ${this.selectedSlot ? html`
            <div class="detail-row">
              <span class="detail-label">Date</span>
              <span class="detail-value">${formatDate(this.selectedSlot, this.timezone)}</span>
            </div>
            ${this.showTime ? html`
              <div class="detail-row">
                <span class="detail-label">Time</span>
                <span class="detail-value">${formatTime(this.selectedSlot, this.timezone)}</span>
              </div>
            ` : ''}
          ` : ''}
          <div class="detail-row">
            <span class="detail-label">Covers</span>
            <span class="detail-value">${this._guestsLabel}</span>
          </div>
          ${this.ticketed && this.discountLabel && this.discountCents > 0 ? html`
            <div class="detail-row discount-row">
              <span class="detail-label">${this.discountLabel}</span>
              <span class="detail-value">−${this._formatAmount(this.discountCents, this.currency)}</span>
            </div>
          ` : ''}
          ${this.comped && !(this.amountCents > 0) ? html`
            <div class="detail-row">
              <span class="detail-label">Total</span>
              <span class="detail-value paid">Complimentary</span>
            </div>
          ` : this.amountCents > 0 ? html`
            <div class="detail-row">
              <span class="detail-label">Total paid</span>
              <span class="detail-value paid">${this._formatAmount(this.amountCents, this.currency)}</span>
            </div>
          ` : ''}
          ${this.name ? html`
            <div class="detail-row">
              <span class="detail-label">Name</span>
              <span class="detail-value">${this.name}</span>
            </div>
          ` : ''}
        </div>
        ${this._renderActions()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'yebo-step-confirm': YeboStepConfirm;
  }
}
